import { useState, useEffect } from 'react';
import {
    Loader2, Plus, RefreshCw, MessageSquare,
    CheckCircle, AlertCircle, Pencil, Trash2,
    ChevronUp, ChevronDown, Eye
} from 'lucide-react';
import { ReadOnlyNote } from './ui/ReadOnlyFieldset';
import Button from './ui/Button';
import TemplateEditorModal from './TemplateEditorModal';
import TemplatePreviewPanel from './TemplatePreviewPanel';
import templateAdminService from '../services/templateAdminService';
import './TemplatesManagement.css';

/**
 * TemplatesManagement component
 *
 * Admin tab for managing Meeting AI question templates.
 * Follows the PlansManagement pattern: self-contained data fetching,
 * local state management, loading/empty/table states.
 *
 * Columns: Rank | Template Key | English Text | Summary | Active | Usage | Actions
 */
const TemplatesManagement = ({ readOnly = false }) => {
    const [templates, setTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [deletingTemplate, setDeletingTemplate] = useState(null);
    const [editingTemplate, setEditingTemplate] = useState(null); // null=closed, {}=create, template=edit
    const [showRecalibrateConfirm, setShowRecalibrateConfirm] = useState(false);
    const [isRecalibrating, setIsRecalibrating] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [refreshCounter, setRefreshCounter] = useState(0);

    // Clear status after 5 seconds
    useEffect(() => {
        if (status.message) {
            const timer = setTimeout(() => setStatus({ type: '', message: '' }), 5000);
            return () => clearTimeout(timer);
        }
    }, [status]);

    const fetchTemplates = async () => {
        setIsLoading(true);
        try {
            const data = await templateAdminService.fetchAllTemplates();
            setTemplates(data);
            setRefreshCounter(prev => prev + 1);
        } catch (error) {
            setStatus({ type: 'error', message: error.message || 'Failed to load templates' });
        } finally {
            setIsLoading(false);
        }
    };

    // Load on mount
    useEffect(() => {
        fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDeleteConfirm = async () => {
        if (readOnly) return;
        if (!deletingTemplate) return;
        try {
            await templateAdminService.deleteTemplate(deletingTemplate.templateKey);
            setStatus({ type: 'success', message: `Template "${deletingTemplate.templateKey}" deleted` });
            setDeletingTemplate(null);
            fetchTemplates();
        } catch (error) {
            setStatus({ type: 'error', message: error.message || 'Failed to delete template' });
            setDeletingTemplate(null);
        }
    };

    const handleToggleActive = async (template) => {
        if (readOnly) return;
        try {
            await templateAdminService.updateTemplate(template.templateKey, {
                ...template,
                isActive: !template.isActive,
            });
            setStatus({
                type: 'success',
                message: `Template "${template.templateKey}" ${!template.isActive ? 'activated' : 'deactivated'}`,
            });
            fetchTemplates();
        } catch (error) {
            setStatus({ type: 'error', message: error.message || 'Failed to update template' });
        }
    };

    const handleRankAdjust = async (template, newRank) => {
        if (readOnly) return;
        try {
            await templateAdminService.adjustTemplateRank(template.templateKey, newRank);
            setStatus({ type: 'success', message: `Rank updated for "${template.templateKey}"` });
            fetchTemplates();
        } catch (error) {
            setStatus({ type: 'error', message: error.message || 'Failed to adjust rank' });
        }
    };

    const handleRecalibrate = async () => {
        if (readOnly) return;
        setIsRecalibrating(true);
        try {
            await templateAdminService.recalibrateRankings();
            setStatus({ type: 'success', message: 'Rankings recalibrated from usage data' });
            setShowRecalibrateConfirm(false);
            fetchTemplates();
        } catch (error) {
            setStatus({ type: 'error', message: error.message || 'Failed to recalibrate rankings' });
            setShowRecalibrateConfirm(false);
        } finally {
            setIsRecalibrating(false);
        }
    };

    const getRankDisplay = (template) => {
        if (template.rank === 0) return <span className="rank-badge rank-top">0</span>;
        return <span className="rank-badge">{template.rank}</span>;
    };

    const getEnglishText = (template) => {
        const text = template.translations && template.translations['en'];
        if (!text) return <span className="template-text-preview no-translation">No English text</span>;
        const truncated = text.length > 50 ? text.slice(0, 50) + '…' : text;
        return <span className="template-text-preview" title={text}>{truncated}</span>;
    };

    return (
        <div className="templates-management">
            {/* Toolbar */}
            <div className="templates-toolbar">
                <div className="templates-toolbar-left">
                    {/* Every template write is level 4; a platform reader keeps Preview and Refresh. */}
                    {!readOnly && (<>
                    <Button
                        variant="primary"
                        icon={<Plus size={16} />}
                        onClick={() => setEditingTemplate({})}
                    >
                        New Template
                    </Button>
                    <Button
                        variant="ghost"
                        icon={<RefreshCw size={15} />}
                        onClick={() => setShowRecalibrateConfirm(true)}
                        data-testid="recalibrate-btn"
                    >
                        Recalibrate
                    </Button>
                    </>)}
                    <Button
                        variant="ghost"
                        icon={<Eye size={15} />}
                        onClick={() => setShowPreview(prev => !prev)}
                    >
                        {showPreview ? 'Hide Preview' : 'Preview'}
                    </Button>
                </div>
                <div className="templates-toolbar-right">
                    <button
                        className="refresh-icon-btn"
                        onClick={fetchTemplates}
                        disabled={isLoading}
                        data-tooltip="Refresh"
                        aria-label="Refresh templates"
                        data-testid="refresh-btn"
                    >
                        <RefreshCw size={20} className={isLoading ? 'rotating' : ''} />
                    </button>
                </div>
            </div>

            <ReadOnlyNote readOnly={readOnly} />

            {/* Status Message */}
            {status.message && (
                <div className={`templates-status ${status.type}`} data-testid="templates-status">
                    {status.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                    {status.message}
                </div>
            )}

            {/* Content */}
            {isLoading ? (
                <div className="templates-loading">
                    <Loader2 className="animate-spin" size={40} />
                    <p>Loading templates...</p>
                </div>
            ) : templates.length === 0 ? (
                <div className="templates-empty">
                    <MessageSquare size={48} strokeWidth={1.5} className="templates-empty-icon" />
                    <h3>No Question Templates</h3>
                    <p>Click "New Template" to create your first question template for the Meeting AI panel.</p>
                </div>
            ) : (
                <div className="templates-table-wrapper">
                    <table className="templates-table">
                        <thead>
                            <tr>
                                <th>Rank</th>
                                <th>Template Key</th>
                                <th>English Text</th>
                                <th>Summary</th>
                                <th>Active</th>
                                <th style={{ textAlign: 'right' }}>Usage</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {templates.map((template, index) => (
                                <tr key={template.templateKey}>
                                    <td>
                                        <div className="rank-cell">
                                            {getRankDisplay(template)}
                                            {!readOnly && (
                                            <div className="rank-controls">
                                                <button
                                                    className="rank-btn"
                                                    onClick={() => handleRankAdjust(template, template.rank - 1)}
                                                    disabled={template.rank === 0 || template.isSummary}
                                                    aria-label={`Move ${template.templateKey} up`}
                                                    data-testid={`rank-up-${template.templateKey}`}
                                                >
                                                    <ChevronUp size={14} />
                                                </button>
                                                <button
                                                    className="rank-btn"
                                                    onClick={() => handleRankAdjust(template, template.rank + 1)}
                                                    disabled={index === templates.length - 1}
                                                    aria-label={`Move ${template.templateKey} down`}
                                                    data-testid={`rank-down-${template.templateKey}`}
                                                >
                                                    <ChevronDown size={14} />
                                                </button>
                                            </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="template-key-cell">{template.templateKey}</td>
                                    <td>{getEnglishText(template)}</td>
                                    <td>
                                        {template.isSummary && (
                                            <span className="summary-badge" data-testid={`summary-badge-${template.templateKey}`}>
                                                Summary
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        {readOnly ? (
                                            <span className={`active-badge ${template.isActive ? 'is-active' : 'is-inactive'}`}>
                                                {template.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        ) : (
                                        <button
                                            className="active-toggle-btn"
                                            onClick={() => handleToggleActive(template)}
                                            data-tooltip={template.isActive ? 'Deactivate' : 'Activate'}
                                            aria-label={`Toggle active status for ${template.templateKey}`}
                                            data-testid={`toggle-active-${template.templateKey}`}
                                        >
                                            <span className={`active-badge ${template.isActive ? 'is-active' : 'is-inactive'}`}>
                                                {template.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </button>
                                        )}
                                    </td>
                                    <td className="usage-count-cell">{template.usageCount || 0}</td>
                                    <td>
                                        {!readOnly && (
                                        <div className="template-actions">
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => setEditingTemplate(template)}
                                                data-tooltip="Edit Template"
                                                aria-label={`Edit template ${template.templateKey}`}
                                            >
                                                <Pencil size={14} />
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="delete-btn"
                                                onClick={() => setDeletingTemplate(template)}
                                                data-tooltip="Delete Template"
                                                aria-label={`Delete template ${template.templateKey}`}
                                                data-testid={`delete-btn-${template.templateKey}`}
                                            >
                                                <Trash2 size={14} />
                                            </Button>
                                        </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Preview Panel */}
            {showPreview && (
                <TemplatePreviewPanel refreshKey={refreshCounter} />
            )}

            {/* Recalibrate Confirmation Modal */}
            {showRecalibrateConfirm && (
                <div className="modal-overlay" onClick={() => !isRecalibrating && setShowRecalibrateConfirm(false)}>
                    <div className="template-delete-modal" onClick={e => e.stopPropagation()}>
                        <h3>Recalibrate Rankings</h3>
                        <p>
                            Reorder templates by popularity (usage data)? The summary template will always stay at rank 0.
                        </p>
                        <div className="modal-actions">
                            <Button
                                variant="ghost"
                                onClick={() => setShowRecalibrateConfirm(false)}
                                disabled={isRecalibrating}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleRecalibrate}
                                isLoading={isRecalibrating}
                                disabled={isRecalibrating}
                                data-testid="confirm-recalibrate-btn"
                            >
                                Recalibrate
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create/Edit Template Modal */}
            {editingTemplate !== null && (
                <TemplateEditorModal
                    template={Object.keys(editingTemplate).length === 0 ? null : editingTemplate}
                    onSave={() => {
                        setEditingTemplate(null);
                        setStatus({ type: 'success', message: 'Template saved successfully' });
                        fetchTemplates();
                    }}
                    onClose={() => setEditingTemplate(null)}
                />
            )}

            {/* Delete Confirmation Modal */}
            {deletingTemplate && (
                <div className="modal-overlay" onClick={() => setDeletingTemplate(null)}>
                    <div className="template-delete-modal" onClick={e => e.stopPropagation()}>
                        <h3>Delete Template</h3>
                        <p>
                            Are you sure you want to delete <strong>{deletingTemplate.templateKey}</strong>?
                        </p>
                        <p className="warning-text">
                            This action cannot be undone. Users will no longer see this template in the Meeting AI panel.
                        </p>
                        <div className="modal-actions">
                            <Button variant="ghost" onClick={() => setDeletingTemplate(null)}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                className="delete-confirm-btn"
                                onClick={handleDeleteConfirm}
                                data-testid="confirm-delete-btn"
                            >
                                Delete Template
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TemplatesManagement;
